
import React, { useState, useEffect } from 'react';
import { Search, Edit2, Calendar, User, ChevronUp, ChevronDown, X, Eye, Loader2 } from 'lucide-react';

interface CommentaryDataGridProps {
  categoryId: string;
  data: any[];
  loading: boolean;
  onEdit: (record: any) => void;
  onRefresh: () => void;
  onPreview?: (record: any) => void;
  onPublish?: () => void;
  onCancelAll?: () => void;
  onCancelChange?: (record: any) => void;
  pendingChanges?: any[];
  driversSubView?: 'commentary' | 'pricechanges';
}

type SortDirection = 'asc' | 'desc';

interface SortCriteria {
  field: string;
  direction: SortDirection;
}

const CommentaryDataGrid: React.FC<CommentaryDataGridProps> = ({
  categoryId,
  data,
  loading,
  onEdit,
  onRefresh,
  onPreview,
  onPublish,
  onCancelAll,
  onCancelChange,
  pendingChanges = [],
  driversSubView = 'commentary',
}) => {
  const getStorageKey = (key: string) => `commentaryGrid_${categoryId}_${key}`;

  const loadFromStorage = <T,>(key: string, defaultValue: T): T => {
    try {
      const stored = localStorage.getItem(getStorageKey(key));
      return stored ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const saveToStorage = (key: string, value: any) => {
    try {
      localStorage.setItem(getStorageKey(key), JSON.stringify(value));
    } catch {
    }
  };

  const [searchTerm, setSearchTerm] = useState(() => loadFromStorage('searchTerm', ''));
  const [sortCriteria, setSortCriteria] = useState<SortCriteria[]>(() => {
    const saved = loadFromStorage<SortCriteria[]>('sortCriteria', []);
    if (saved.length > 0) {
      return saved;
    }
    if (categoryId === 'forecast') {
      return [
        { field: 'forecastYear', direction: 'asc' },
        { field: 'forecastQtr', direction: 'asc' }
      ];
    }
    return [{ field: 'date', direction: 'desc' }];
  });
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => loadFromStorage('columnFilters', {}));

  useEffect(() => {
    saveToStorage('searchTerm', searchTerm);
  }, [searchTerm]);


  useEffect(() => {
    saveToStorage('sortCriteria', sortCriteria);
  }, [sortCriteria]);

  useEffect(() => {
    saveToStorage('columnFilters', columnFilters);
  }, [columnFilters]);

  useEffect(() => {
    const saved = loadFromStorage<SortCriteria[]>('sortCriteria', []);
    if (saved.length === 0) {
      if (categoryId === 'forecast') {
        setSortCriteria([
          { field: 'forecastYear', direction: 'asc' },
          { field: 'forecastQtr', direction: 'asc' }
        ]);
      } else {
        setSortCriteria([{ field: 'date', direction: 'desc' }]);
      }
    } else {
      setSortCriteria(saved);
    }
  }, [categoryId]);

  const hasPendingChanges = (record: any): boolean => {
    const supportedCategories = ['forecast', 'drivers', 'peacc', 'ppacc', 'psacc', 'pvcacc'];
    if (!supportedCategories.includes(categoryId) || !pendingChanges.length) {
      return false;
    }

    return pendingChanges.some(pending => {
      if (categoryId === 'forecast') {
        const pendingYear = pending.forecastQtrDateId
          ? parseInt(pending.forecastQtrDateId.split('-')[0])
          : null;

        const pendingQtr = parseInt(String(pending.forecastQtr));
        const recordQtr = parseInt(String(record.forecastQtr));
        const recordYear = parseInt(String(record.forecastYear));

        return pendingYear === recordYear &&
               pendingQtr === recordQtr &&
               pending.polymer === record.polymer;
      } else if (categoryId === 'drivers') {
        if (driversSubView === 'pricechanges') {
          const pendingDate = typeof pending.date === 'string' ? pending.date : pending.date?.toString();
          const recordDate = typeof record.date === 'string' ? record.date : record.date?.toString();
          return pendingDate === recordDate;
        }

        const pendingDate = typeof pending.date === 'string' ? pending.date : pending.date?.toString();
        const recordDate = typeof record.date === 'string' ? record.date : record.date?.toString();

        return pendingDate === recordDate &&
               pending.driverReport === record.driverReport;
      } else if (['peacc', 'ppacc', 'psacc', 'pvcacc'].includes(categoryId)) {
        // For ACC commentary types, match by date only
        const pendingDate = typeof pending.date === 'string' ? pending.date : pending.date?.toString();
        const recordDate = typeof record.date === 'string' ? record.date : record.date?.toString();

        return pendingDate === recordDate;
      }
      return false;
    });
  };

  useEffect(() => {
    let filtered = [...data];

    if (categoryId === 'forecast') {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      const currentQtr = Math.ceil(currentMonth / 3);

      filtered = filtered.filter((record) => {
        const recordYear = record.forecastYear;
        const recordQtr = record.forecastQtr;

        return recordYear > currentYear || (recordYear === currentYear && recordQtr >= currentQtr);
      });
    }

    if (searchTerm) {
      filtered = filtered.filter((record) => {
        const searchLower = searchTerm.toLowerCase();
        return Object.values(record).some((value) => {
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchLower);
        });
      });
    }

    Object.entries(columnFilters).forEach(([field, filterValue]) => {
      if (filterValue) {
        filtered = filtered.filter((record) => {
          const value = record[field];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });

    if (sortCriteria.length > 0) {
      filtered.sort((a, b) => {
        for (const criteria of sortCriteria) {
          const aVal = a[criteria.field];
          const bVal = b[criteria.field];

          if (aVal === null || aVal === undefined) {
            if (bVal === null || bVal === undefined) continue;
            return 1;
          }
          if (bVal === null || bVal === undefined) return -1;

          let comparison = 0;
          if (typeof aVal === 'string') {
            comparison = aVal.localeCompare(bVal);
          } else if (typeof aVal === 'number') {
            comparison = aVal - bVal;
          } else if (aVal instanceof Date) {
            comparison = aVal.getTime() - bVal.getTime();
          }

          if (comparison !== 0) {
            return criteria.direction === 'asc' ? comparison : -comparison;
          }
        }
        return 0;
      });
    }

    setFilteredData(filtered);
  }, [data, searchTerm, sortCriteria, columnFilters, pendingChanges]);

  const handleSort = (field: string) => {
    const existingIndex = sortCriteria.findIndex(c => c.field === field);

    if (existingIndex >= 0) {
      const existing = sortCriteria[existingIndex];
      if (existing.direction === 'asc') {
        const newCriteria = [...sortCriteria];
        newCriteria[existingIndex] = { field, direction: 'desc' };
        setSortCriteria(newCriteria);
      } else {
        setSortCriteria(sortCriteria.filter(c => c.field !== field));
      }
    } else {
      setSortCriteria([...sortCriteria, { field, direction: 'asc' }]);
    }
  };

  const clearSort = () => {
    let defaultSort: SortCriteria[];
    if (categoryId === 'forecast') {
      defaultSort = [
        { field: 'forecastYear', direction: 'asc' },
        { field: 'forecastQtr', direction: 'asc' }
      ];
    } else {
      defaultSort = [{ field: 'date', direction: 'desc' }];
    }
    setSortCriteria(defaultSort);
    saveToStorage('sortCriteria', defaultSort);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setColumnFilters({});
    saveToStorage('searchTerm', '');
    saveToStorage('columnFilters', {});
  };

  const getSortInfo = (field: string): { direction: SortDirection; level: number } | null => {
    const index = sortCriteria.findIndex(c => c.field === field);
    if (index === -1) return null;
    return {
      direction: sortCriteria[index].direction,
      level: index + 1
    };
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';

    try {
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const month = parseInt(parts[0], 10);
          const day = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          const date = new Date(year, month - 1, day);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        }
      }

      const [datePart] = dateStr.split('T');
      const [year, month, day] = datePart.split('-');

      if (!year || !month || !day) return dateStr;

      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

      if (isNaN(date.getTime())) return dateStr;

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    const src = dateStr.includes('Z') || dateStr.includes('+') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
    const date = new Date(src);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return '-';
    const stripped = text.replace(/<[^>]*>/g, '');
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
  };

  const renderSortIcon = (field: string) => {
    const sortInfo = getSortInfo(field);
    if (!sortInfo) return null;

    const Icon = sortInfo.direction === 'asc' ? ChevronUp : ChevronDown;

    return (
      <span className="inline-flex items-center ml-1">
        <Icon size={16} className="text-rs-green" />
        {sortCriteria.length > 1 && (
          <span className="text-xs ml-0.5 text-rs-green font-bold">
            {sortInfo.level}
          </span>
        )}
      </span>
    );
  };

  const getColumns = () => {
    if (categoryId === 'drivers' && driversSubView === 'pricechanges') {
      return [
        { field: 'date', label: 'Date', sortable: true, filterable: true },
        { field: 'PE', label: 'PE', sortable: false, filterable: false },
        { field: 'PP', label: 'PP', sortable: false, filterable: false },
        { field: 'PS', label: 'PS', sortable: false, filterable: false },
        { field: 'ABS', label: 'ABS', sortable: false, filterable: false },
        { field: 'PC', label: 'PC', sortable: false, filterable: false },
        { field: 'PVC', label: 'PVC', sortable: false, filterable: false },
        { field: 'PA66', label: 'PA66', sortable: false, filterable: false },
        { field: 'PA6', label: 'PA6', sortable: false, filterable: false },
        { field: 'PET', label: 'PET', sortable: false, filterable: false },
      ];
    }

    switch (categoryId) {
      case 'drivers':
        return [
          { field: 'date', label: 'Date', sortable: true, filterable: true },
          { field: 'driverReport', label: 'Driver Report', sortable: true, filterable: true },
          { field: 'title', label: 'Title', sortable: true, filterable: true },
          { field: 'marketOverviewBody', label: 'Market Overview', sortable: false, filterable: true },
          { field: 'lastUpdatedBy', label: 'Updated By', sortable: true, filterable: true },
          { field: 'lastUpdatedTime', label: 'Updated', sortable: true, filterable: true },
        ];
      case 'peacc':
        return [
          { field: 'date', label: 'Date', sortable: true },
          { field: 'pe_inv_cum_gains_draws', label: 'PE Inventory Cum. Gains/Draws', sortable: false },
          { field: 'pe_prod_sales', label: 'PE Production/Sales', sortable: false },
          { field: 'hdpe', label: 'HDPE', sortable: false },
          { field: 'ldpe', label: 'LDPE', sortable: false },
          { field: 'lldpe', label: 'LLDPE', sortable: false },
          { field: 'last_updated_by', label: 'Updated By', sortable: true },
          { field: 'last_updated_time', label: 'Updated', sortable: true },
        ];
      case 'ppacc':
        return [
          { field: 'date', label: 'Date', sortable: true },
          { field: 'pp_supply_demand', label: 'PP Supply/Demand', sortable: false },
          { field: 'last_updated_by', label: 'Updated By', sortable: true },
          { field: 'last_updated_time', label: 'Updated', sortable: true },
        ];
      case 'psacc':
        return [
          { field: 'date', label: 'Date', sortable: true },
          { field: 'ps_inv_cum_gains_draws', label: 'PS Inventory Cum. Gains/Draws', sortable: false },
          { field: 'ps_supply_demand', label: 'PS Supply/Demand', sortable: false },
          { field: 'ps_styrene_exports', label: 'PS Styrene Exports', sortable: false },
          { field: 'ps_benzene_imports', label: 'PS Benzene Imports', sortable: false },
          { field: 'ps_butadiene_imports', label: 'PS Butadiene Imports', sortable: false },
          { field: 'last_updated_by', label: 'Updated By', sortable: true },
          { field: 'last_updated_time', label: 'Updated', sortable: true },
        ];
      case 'pvcacc':
        return [
          { field: 'date', label: 'Date', sortable: true },
          { field: 'pvc_inv_cum_gains_draws', label: 'PVC Inventory Cum. Gains/Draws', sortable: false },
          { field: 'pvc_prod_sales', label: 'PVC Production/Sales', sortable: false },
          { field: 'pvc_exports', label: 'PVC Exports', sortable: false },
          { field: 'pvc_domestic_sales', label: 'PVC Domestic Sales', sortable: false },
          { field: 'last_updated_by', label: 'Updated By', sortable: true },
          { field: 'last_updated_time', label: 'Updated', sortable: true },
        ];
      case 'forecast':
        return [
          { field: 'forecastYear', label: 'Year', sortable: true },
          { field: 'forecastQtr', label: 'Quarter', sortable: true },
          { field: 'polymer', label: 'Polymer', sortable: true },
          { field: 'commentary', label: 'Commentary', sortable: false },
          { field: 'lastUpdatedBy', label: 'Updated By', sortable: true },
          { field: 'lastUpdatedTime', label: 'Updated', sortable: true },
        ];
      case 'daily':
        return [
          { field: 'date', label: 'Date', sortable: true, filterable: true },
          { field: 'spot_ethylene', label: 'Spot Ethylene', sortable: false },
          { field: 'spot_rgp', label: 'Spot RGP', sortable: false },
          { field: 'spot_pgp', label: 'Spot PGP', sortable: false },
          { field: 'spot_benzene', label: 'Spot Benzene', sortable: false },
          { field: 'spot_styrene', label: 'Spot Styrene', sortable: false },
          { field: 'spot_butadiene', label: 'Spot Butadiene', sortable: false },
          { field: 'vam', label: 'VAM', sortable: false },
          { field: 'ethylene_contract', label: 'Ethylene Contract', sortable: false },
          { field: 'pgp_contract', label: 'PGP Contract', sortable: false },
          { field: 'last_updated', label: 'Updated', sortable: true },
        ];
      default:
        return [];
    }
  };

  const columns = getColumns();

  return (
    <div className="commentary-data-grid relative">
      {loading && data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <Loader2 className="w-8 h-8 text-rs-green animate-spin" />
          <div className="text-rs-off-white/60">Loading data...</div>
        </div>
      ) : (
        <>
          {loading && data.length > 0 && (
            <>
              <div className="absolute inset-0 bg-rs-black/70 backdrop-blur-sm z-10 rounded-lg" />
              <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center justify-center gap-4 pointer-events-none">
                <Loader2 className="w-10 h-10 text-rs-green animate-spin" />
                <div className="text-rs-off-white font-medium">Refreshing data...</div>
              </div>
            </>
          )}
          <div className="mb-4 flex items-center gap-4">
        <div className="flex-1 relative">
          <Search
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-rs-off-white/50"
          />
          <input
            type="text"
            placeholder="Search records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-rs-jet border border-rs-jet-light rounded pl-10 pr-4 py-2 text-rs-off-white placeholder-rs-off-white/50 focus:border-rs-green focus:outline-none"
          />
        </div>
        {(searchTerm || Object.values(columnFilters).some(v => v)) && (
          <button
            onClick={clearAllFilters}
            className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white px-4 py-2 rounded transition-colors flex items-center gap-2"
          >
            <X size={16} />
            Clear Filters
          </button>
        )}
        <button
          onClick={onRefresh}
          className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white px-4 py-2 rounded transition-colors"
        >
          Refresh
        </button>
      </div>

      <div className="mb-4 flex items-center gap-4 flex-wrap">
        {(['forecast', 'drivers', 'peacc', 'ppacc', 'psacc', 'pvcacc'].includes(categoryId)) && pendingChanges.length > 0 && (
          <>
            {onPreview && (
              <button
                onClick={() => onPreview(null)}
                className="bg-rs-salmon hover:bg-rs-salmon/80 !text-rs-off-black font-semibold px-4 py-2 rounded transition-colors flex items-center gap-2"
              >
                <Eye size={18} />
                Preview Pending Changes ({pendingChanges.length})
              </button>
            )}
            {onPublish && (
              <button
                onClick={onPublish}
                className="bg-rs-green hover:bg-rs-light-green !text-rs-off-black font-semibold px-4 py-2 rounded transition-colors flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Publish Changes
              </button>
            )}
            {onCancelAll && (
              <button
                onClick={onCancelAll}
                className="bg-rs-red/70 hover:bg-rs-red text-white font-semibold px-4 py-2 rounded transition-colors flex items-center gap-2"
              >
                <X size={18} />
                Cancel All Changes
              </button>
            )}
          </>
        )}
      </div>

      {sortCriteria.length > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-rs-off-white/70">Sorted by:</span>
          {sortCriteria.map((criteria, index) => (
            <span
              key={criteria.field}
              className="inline-flex items-center gap-1 bg-rs-green/20 text-rs-green px-2 py-1 rounded text-sm"
            >
              <span className="font-semibold">{index + 1}.</span>
              {getColumns().find(c => c.field === criteria.field)?.label || criteria.field}
              {criteria.direction === 'asc' ? ' ↑' : ' ↓'}
            </span>
          ))}
          <button
            onClick={clearSort}
            className="inline-flex items-center gap-1 text-rs-off-white/70 hover:text-rs-off-white text-sm transition-colors"
          >
            <X size={16} />
            Clear all
          </button>
        </div>
      )}

      <div className="mb-3 text-sm text-rs-off-white/70">
        {filteredData.length} of {data.length} records
        {searchTerm && ` matching "${searchTerm}"`}
      </div>

      {filteredData.length === 0 ? (
        <div className="text-center py-12 text-rs-off-white/50">
          {data.length === 0 ? (
            <>
              <Calendar size={48} className="mx-auto mb-4 opacity-30" />
              <p>No records found</p>
              <p className="text-xs mt-2">Create a new record to get started</p>
            </>
          ) : (
            <>
              <Search size={48} className="mx-auto mb-4 opacity-30" />
              <p>No records match your search</p>
              <p className="text-xs mt-2">Try a different search term</p>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto border border-rs-jet-light rounded-lg">
          <table className="w-full">
            <thead className="bg-rs-jet border-b border-rs-jet-light">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.field}
                    className="px-4 py-3 text-left text-sm font-semibold text-rs-green"
                  >
                    <div className="flex flex-col gap-2">
                      <div
                        className={column.sortable ? 'cursor-pointer hover:bg-rs-jet-light rounded px-2 py-1' : 'rounded px-2 py-1'}
                        onClick={() => column.sortable && handleSort(column.field)}
                      >
                        {column.label}
                        {column.sortable && renderSortIcon(column.field)}
                      </div>
                      {column.filterable !== false && (
                        <input
                          type="text"
                          placeholder={`Filter ${column.label}...`}
                          value={columnFilters[column.field] || ''}
                          onChange={(e) => setColumnFilters(prev => ({ ...prev, [column.field]: e.target.value }))}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full bg-rs-black border border-rs-jet-light rounded px-2 py-1 text-xs text-rs-off-white placeholder-rs-off-white/30 focus:border-rs-green focus:outline-none"
                        />
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-sm font-semibold text-[#67B640]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#161814]">
              {filteredData.map((record, index) => {
                const hasChanges = hasPendingChanges(record);
                return (
                  <tr
                    key={index}
                    className={`border-b border-rs-jet-light transition-colors ${
                      hasChanges 
                        ? 'bg-rs-salmon/10 hover:bg-rs-salmon/20 border-l-4 border-l-rs-salmon' 
                        : 'hover:bg-rs-jet'
                    }`}
                  >
                  {columns.map((column) => (
                    <td key={column.field} className="px-4 py-3 text-sm text-rs-off-white">
                      {column.field === 'date' || column.field === 'forecastQtrDateId'
                        ? formatDate(record[column.field])
                        : column.field === 'lastUpdatedTime' ||
                          column.field === 'last_updated_time'
                        ? formatDateTime(record[column.field])
                        : column.field === 'lastUpdatedBy' ||
                          column.field === 'last_updated_by'
                        ? (
                          <span className="flex items-center gap-1">
                            <User size={14} className="text-rs-green" />
                            {record[column.field] || '-'}
                          </span>
                        )
                        : column.field === 'forecastQtr'
                        ? `Q${record[column.field]}`
                        : typeof record[column.field] === 'string' &&
                          record[column.field].length > 50
                        ? truncateText(record[column.field], 100)
                        : record[column.field] || '-'}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {(['forecast', 'drivers', 'peacc', 'ppacc', 'psacc', 'pvcacc'].includes(categoryId)) && onPreview && hasChanges && (
                        <button
                          onClick={() => onPreview(record)}
                          className="flex items-center gap-1 text-rs-salmon hover:text-rs-salmon/80 transition-colors"
                          title="Preview pending changes"
                        >
                          <Eye size={16} />
                          <span className="text-sm">Preview</span>
                        </button>
                      )}
                      {(['forecast', 'drivers', 'peacc', 'ppacc', 'psacc', 'pvcacc'].includes(categoryId)) && onCancelChange && hasChanges && (
                        <button
                          onClick={() => onCancelChange(record)}
                          className="flex items-center gap-1 text-rs-red hover:text-rs-red/70 transition-colors"
                          title="Cancel pending change"
                        >
                          <X size={16} />
                          <span className="text-sm">Cancel</span>
                        </button>
                      )}
                      <button
                        onClick={() => onEdit(record)}
                        className="flex items-center gap-1 text-rs-green hover:text-rs-light-green transition-colors"
                        title="Edit record"
                      >
                        <Edit2 size={16} />
                        <span className="text-sm">Edit</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default CommentaryDataGrid;

